import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { customFieldsApi, type CustomFieldWithValue } from '@services/api/customFields';
import type { ProjectMember } from '@services/api/projects';

export default function TaskCustomFields({ taskId, members }: { taskId: string; members: ProjectMember[] }) {
  const { t } = useTranslation();
  const [fields, setFields] = useState<CustomFieldWithValue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void customFieldsApi.getTaskFields(taskId).then((data) => {
      if (!cancelled) setFields(data);
    }).catch(() => {
      if (!cancelled) setError(t('settings.customFields.failedToLoad'));
    });
    return () => { cancelled = true; };
  }, [taskId, t]);

  const save = async (field: CustomFieldWithValue, value: unknown) => {
    if (JSON.stringify(value) === JSON.stringify(field.currentValue ?? null)) return;
    setSaving(field.id);
    setError(null);
    try {
      await customFieldsApi.bulkSetValues(taskId, { [field.id]: value });
      setFields((items) => items.map((item) => item.id === field.id ? { ...item, currentValue: value } : item));
    } catch {
      setError(t('settings.customFields.failedToUpdate'));
    } finally {
      setSaving(null);
    }
  };

  if (!fields.length && !error) return null;
  const inputClass = 'w-full rounded-lg border border-neutral-300 bg-transparent px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50 dark:border-neutral-700';
  return <section className="mt-6" aria-label={t('settings.customFields.title')}>
    <h3 className="mb-3 text-sm font-medium text-neutral-500">{t('settings.customFields.title')}</h3>
    {error && <p role="alert" className="mb-3 text-sm text-red-600">{error}</p>}
    <div className="grid gap-4 sm:grid-cols-2">
      {fields.map((field) => {
        const id = `task-field-${field.id}`;
        const value: unknown = field.currentValue;
        const disabled = saving !== null;
        const props = { id, className: inputClass, disabled, required: field.isRequired };
        return <div key={field.id}>
          <label htmlFor={id} className="mb-1 block text-sm font-medium">{field.name}{field.isRequired ? ' *' : ''}</label>
          {field.fieldType === 'checkbox' ? <input id={id} type="checkbox" disabled={disabled}
            checked={value === true} onChange={(event) => void save(field, event.target.checked)} />
          : field.fieldType === 'select' || field.fieldType === 'multiselect' || field.fieldType === 'user' ?
            <select {...props} multiple={field.fieldType === 'multiselect'}
              value={field.fieldType === 'multiselect' ? (Array.isArray(value) ? value.map(String) : []) : String(value ?? '')}
              onChange={(event) => void save(field, field.fieldType === 'multiselect'
                ? Array.from(event.target.selectedOptions, (option) => option.value) : event.target.value || null)}>
              {field.fieldType !== 'multiselect' && <option value="">{t('common.none', 'None')}</option>}
              {field.fieldType === 'user'
                ? members.map((member) => <option key={member.user.id} value={member.user.id}>{member.user.displayName}</option>)
                : field.options?.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          : field.fieldType === 'textarea' ? <textarea {...props} defaultValue={String(value ?? '')}
              onBlur={(event) => void save(field, event.target.value || null)} />
          : <input {...props} type={field.fieldType === 'datetime' ? 'datetime-local' : field.fieldType}
              defaultValue={String(value ?? '')} step={field.fieldType === 'number' ? 'any' : undefined}
              onBlur={(event) => {
                if (!event.target.reportValidity()) return;
                const raw = event.target.value;
                void save(field, raw === '' ? null : field.fieldType === 'number' ? Number(raw) : raw);
              }} />}
          {field.description && <p className="mt-1 text-xs text-neutral-500">{field.description}</p>}
        </div>;
      })}
    </div>
  </section>;
}
